/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {useState} from 'react';
import {useStore} from "zustand";
import dayjs from "dayjs";
import _ from "lodash";
import {Button} from "antd";
import { useMediaQuery } from 'react-responsive';
import classNames from "classnames";
import * as Excel from 'exceljs';
import {FileExcelOutlined} from "@ant-design/icons";
import styles from './PointAdjust.module.scss';
import PageTitle from "../../components/common/PageTitle/PageTitle";
import {emptySearchGroup, GroupSearchSelect, GroupValue} from "../../components/common/selector/GroupSearchSelect";
import PeriodButtons from "./PeriodButtons/PeriodButtons";
import {
	CarTypeType,
	PointAdjustData,
	PointAdjustHistory,
	PointAdjustRankingData,
	RankingPeriodType,
} from "./PointAdjust.interface";
import CarTypes from "./CarTypes/CarTypes";
import {useMessageStore} from "../../stores/messageStore";
import groupAPI from "../../api/groupAPI";
import pointAPI from "../../api/pointAPI";
import apiClient from "../../api/apiClient";
import {PointPeriodType} from "../../types/Type";
import PointAdjustTable from "./PointAdjustTable/PointAdjustTable";
import {useAppStore} from "../../stores/appStore";

const filterByTon = (rankingData: PointAdjustRankingData[], selectedCarType?: CarTypeType) => {
	return rankingData.filter((item: PointAdjustRankingData) => {
		if (selectedCarType === '1t') return item.carType === '1t';
		if (selectedCarType === '2t') return item.carType === '2t';
		if (selectedCarType === '3t') return item.carType === '3t';
		if (selectedCarType === '5t') return item.carType === '5t';
		if (selectedCarType === '5t_over') return (['54m', '58m', '75m'] as CarTypeType[]).includes(item.carType);
		return false;
	});
}

const getInitRankingPeriod = (pointPeriodType: PointPeriodType): RankingPeriodType => {
	switch (pointPeriodType) {
		case 'half_month': return 'this_half_month';
		case 'month': return 'this_month';
		default: return pointPeriodType;
	}
};


const PointAdjust = () => {
	const isTabletOrMobile = useMediaQuery({ query: '(max-width: 1224px)' })
	const { setMessages } = useStore(useMessageStore);
	const setShouldShowSpinner = useAppStore((state) => state.setShouldShowSpinner);
	const [selectedGroup, setSelectedGroup] = useState<GroupValue>(emptySearchGroup);
	const [data, setData] = useState<PointAdjustData>({ configs: {}, rankingData: [] });
	const [filteredRankingData, setFilteredRankingData] = useState<PointAdjustRankingData[]>([]);
	const [pointPeriodType, setPointPeriodType] = useState<PointPeriodType>('month'); // config_group에 기재된 point_period_type
	const [rankingPeriodType, setRankingPeriodType] = useState<RankingPeriodType>('this_month'); // 랭킹 구할 때 필요한 period type
	const [selectedCarType, setSelectedCarType] = useState<CarTypeType | undefined>(undefined);

	const firstDayOfTheLastMonth = +dayjs().subtract(1, 'month').startOf('month').format('YYYYMMDD');
	const firstDayOfTheLastHalfMonth = +dayjs().date() > 15
		? +dayjs().startOf('month').format('YYYYMMDD')
		: +dayjs().subtract(1, 'month').set('date', 16).format('YYYYMMDD');
	const today = +dayjs().format('YYYYMMDD');

	const getAdjustDate = (pointPeriodType: PointPeriodType) => {
		if (pointPeriodType === 'month') return firstDayOfTheLastMonth;
		if (pointPeriodType === 'half_month') return firstDayOfTheLastHalfMonth;
		return today;
	};

	const onClickGroupName = async (value: GroupValue) => {
		if (!value?.key) return;
		setSelectedGroup(value);
		const configs = await groupAPI.getGroupConfigs(value.key);
		const pointPeriodType: PointPeriodType = configs.config.point_period_type;
		setPointPeriodType(pointPeriodType);
		const rankingPeriod = getInitRankingPeriod(pointPeriodType);
		setRankingPeriodType(rankingPeriod);
		setSelectedCarType('all');

		const newData = await groupAPI.getGroupRanking(value.key, rankingPeriod);
		setData(newData);
		// '적용 날짜' 컬럼 기본값 구하기
		const adjustDate = getAdjustDate(pointPeriodType);
		const newFilteredRankingData = newData.rankingData.map((item: PointAdjustRankingData) => {
			return _.omit({ ...item, adjustDate }, ['adjustPoint', 'adjustReason']);
		});
		setFilteredRankingData(newFilteredRankingData);
	};

	// 추가 점수 버튼 누른 뒤 다시 fetch
	const fetchAfterAdjustPoint = async () => {
		const newData = await groupAPI.getGroupRanking(selectedGroup.key, rankingPeriodType);
		setData(newData);

		const adjustDate = getAdjustDate(pointPeriodType);
		const newFilteredRankingData = selectedCarType === 'all'
			? newData.rankingData.map((item: PointAdjustRankingData) => {
					return _.omit({ ...item, adjustDate }, ['adjustPoint', 'adjustReason'])
				})
			: filterByTon(newData.rankingData, selectedCarType).map((item: PointAdjustRankingData) => {
				return _.omit({ ...item, adjustDate }, ['adjustPoint', 'adjustReason'])
			});
		setFilteredRankingData(newFilteredRankingData);
	};

	const onClickBulkEdit = async () => {
		try {
			setShouldShowSpinner(true);
			const targetList = filteredRankingData.filter((item) => item.adjustReason
				&& (item.adjustPoint || item.adjustBaljuCount || item.adjustSujuCount || item.adjustBaljuAmount || item.adjustSujuAmount));
			if (targetList.length === 0) return;

			const promiseList = targetList.map((item) => pointAPI.updatePointAPI({
				memberCarId: item.memberCarId,
				point: item.adjustPoint!,
				reason: item.adjustReason!,
				pointAdjustDay: item.adjustDate || undefined,
				baljuCount: item.adjustBaljuCount,
				sujuCount: item.adjustSujuCount,
				baljuAmount: item.adjustBaljuAmount ? item.adjustBaljuAmount * 10000 : 0,
				sujuAmount: item.adjustSujuAmount ? item.adjustSujuAmount * 10000 : 0,
			}));
			await Promise.all(promiseList);
			await fetchAfterAdjustPoint();
			setMessages('success', '일괄 추가 완료');
		} catch (e) {
			setMessages('error', '일괄 추가 실패');
		} finally {
			setShouldShowSpinner(false);
		}
	};

	const onClickAdjustPointBtn = async (record: PointAdjustRankingData) => {
		try {
			setShouldShowSpinner(true);
			await pointAPI.updatePointAPI({
				memberCarId: record.memberCarId,
				point: record.adjustPoint!,
				reason: record.adjustReason!,
				pointAdjustDay: record.adjustDate || undefined,
				baljuCount: record.adjustBaljuCount,
				sujuCount: record.adjustSujuCount,
				baljuAmount: record.adjustBaljuAmount ? record.adjustBaljuAmount * 10000 : 0,
				sujuAmount: record.adjustSujuAmount ? record.adjustSujuAmount * 10000 : 0,
			});
			await fetchAfterAdjustPoint();
			setMessages('success', `추가 완료 - 조정 사유: ${record.adjustReason}`);
		} catch (e) {
			setMessages('error', `추가 실패 - 조정 사유: ${record.adjustReason}`);
		} finally {
			setShouldShowSpinner(false);
		}
	};

	const onClickRankingPeriodBtn = async (rankingPeriod: RankingPeriodType) => {
		if(!selectedCarType) return;

		setRankingPeriodType(rankingPeriod);
		const newData = await groupAPI.getGroupRanking(selectedGroup.key, rankingPeriod);
		setData(newData);

		const adjustDate = getAdjustDate(pointPeriodType);

		const newFilteredRankingData = selectedCarType === 'all'
			? newData.rankingData.map((item: PointAdjustRankingData) => {
					return _.omit({ ...item, adjustDate }, ['adjustPoint', 'adjustReason']);
				})
			: filterByTon(newData.rankingData, selectedCarType).map((item: PointAdjustRankingData) => {
				return _.omit({ ...item, adjustDate }, ['adjustPoint', 'adjustReason'])
			});
		setFilteredRankingData(newFilteredRankingData);
	};

	const onClickCarTypeBtn = (carType: CarTypeType) => {
		const adjustDate = getAdjustDate(pointPeriodType);

		const newFilteredRankingData = carType === 'all'
			? data.rankingData.map((item: PointAdjustRankingData) => {
				return _.omit({ ...item, adjustDate }, ['adjustPoint', 'adjustReason']);
			})
			: filterByTon(data.rankingData, carType).map((item: PointAdjustRankingData) => {
				return _.omit({ ...item, adjustDate }, ['adjustPoint', 'adjustReason'])
			});

		setFilteredRankingData(newFilteredRankingData);
		setSelectedCarType(carType);
	};


	const getBulkEditButtonDisabled = () => {
		const targetList = filteredRankingData.filter((item) => item.adjustReason
			&& (item.adjustPoint || item.adjustBaljuCount || item.adjustSujuCount || item.adjustBaljuAmount || item.adjustSujuAmount));
		if (targetList.length === 0) return true;

		const isAllDatesValid = targetList.every((item) => item.adjustDate
			&& dayjs(item.adjustDate.toString(), 'YYYYMMDD').isValid());
		return !isAllDatesValid;
	};

	const editHistory = async (history: PointAdjustHistory) => {
		setShouldShowSpinner(true);
		try {
			const { id, pointAdjustDay, point, baljuCount, sujuCount, baljuAmount, sujuAmount, reason } = history;
			await apiClient.put('/api/admin/common/pointAdjustHistory', {
				id,
				pointAdjustDay,
				point,
				baljuCount,
				sujuCount,
				baljuAmount,
				sujuAmount,
				reason,
			});
			await fetchAfterAdjustPoint();
			setMessages('success', `수정 완료`);
		} catch (e) {
			setMessages('error', `수정 실패`);
		} finally {
			setShouldShowSpinner(false);
		}

	};

	const deleteHistory = async (id: string) => {
		try {
			setShouldShowSpinner(true);
			await apiClient.delete(`/api/admin/common/pointAdjustHistory/${id}`);
			await fetchAfterAdjustPoint();
			setMessages('success', '삭제 완료');
		} catch (e) {
			setMessages('error', '삭제 실패');
		} finally {
			setShouldShowSpinner(false);
		}
	};

	const makeExcelSheet = async () => {
		const workbook = new Excel.Workbook();
		const worksheet = workbook.addWorksheet(selectedGroup.name);
		worksheet.columns = [
			{ key: 'ranking', title: '랭킹', width: 10 },
			{ key: 'carType', title: '톤수', width: 10 },
			{ key: 'carNumber', title: '차량번호', width: 20 },
			{ key: 'name', title: '이름', width: 20 },
			{ key: 'nickname', title: '닉네임', width: 20 },
			{ key: 'point', title: '총 점수', width: 10 },
			{ key: 'balju', title: '발주', width: 10 },
			{ key: 'suju', title: '수주', width: 10 },
		].map((item) => ({
			header: item.title, key: item.key, width: item.width,
		}));

		const rows = data.rankingData.map((item) => {
			return {
				ranking: item.rank,
				carType: item.carType,
				carNumber: item.number,
				name: item.name,
				nickname: item.nickname,
				point: item.point,
				balju: item.baljuCount,
				suju: item.sujuCount,
			};
		});

		// 3. 엑셀에 데이터 추가
		worksheet.addRows(rows);

		// 4. 스타일 (헤더 bold)
		worksheet.getRow(1).eachCell((cell) => {
			cell.font = { bold: true };
			cell.alignment = { vertical: 'middle', horizontal: 'center' };
		});

		// 5. 엑셀 파일 생성
		const buffer: Excel.Buffer = await workbook.xlsx.writeBuffer();

		// 6. 브라우저 다운로드 처리
		const blob = new Blob([buffer], {
			type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
		});

		const fileName = `점수조정_${selectedGroup.name}_${dayjs().format('YYYYMMDD')}.xlsx`;

		const link = document.createElement('a');
		link.href = URL.createObjectURL(blob);
		link.download = fileName;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};


	return (
		<div className={styles.wrap}>
			<PageTitle title="점수 조정"/>

			<div className={classNames(styles.top, { [styles.tabletOrMobile]: isTabletOrMobile })}>
				<div className={styles.selectWrap}>
					<GroupSearchSelect
						selectedGroup={selectedGroup}
						onChange={onClickGroupName}
						placeholder="그룹 검색"
					/>
				</div>
				{data.configs.point_period_type && (
					<PeriodButtons
						selectedPointPeriod={pointPeriodType}
						selectedRankingPeriod={rankingPeriodType}
						onClick={onClickRankingPeriodBtn}
					/>
				)}
				{selectedCarType && (
					<CarTypes
						selectedCarType={selectedCarType}
						onClick={onClickCarTypeBtn}
					/>
				)}
				<Button
					style={{ marginLeft: 'auto', backgroundColor: '#1D6F42', color: 'white' }}
					onClick={makeExcelSheet}
				>
					엑셀 파일 내보내기
					<FileExcelOutlined />
				</Button>
				<Button
					className={styles.pointAddBtn}
					onClick={onClickBulkEdit}
					disabled={getBulkEditButtonDisabled()}
				>
					일괄 점수 추가
				</Button>
			</div>

			<div>
				<PointAdjustTable
					filteredRankingData={filteredRankingData}
					setFilteredRankingData={setFilteredRankingData}
					deleteHistory={deleteHistory}
					editHistory={editHistory}
					onClickAdjustPointBtn={onClickAdjustPointBtn}
					selectedGroup={selectedGroup}
				/>
			</div>
		</div>
	);
};

export default PointAdjust;
